import React, { useState, useEffect } from 'react';
import {
  Home,
  DirectionsCar,
  Business,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Assessment
} from '@mui/icons-material';
import { formatCurrency } from '../../../utils/formatters';
import walletService from '../../wallet/services/walletService';

// Helper function to format large numbers with SI prefixes and determine text size
const formatLargeValue = (value) => {
  const absValue = Math.abs(value);
  const isNegative = value < 0;
  
  if (absValue >= 10000000) {
    // 10M+ - Use SI prefixing
    const millions = absValue / 1000000;
    const formatted = millions.toFixed(1);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}M`,
      sizeClass: 'text-[7px] sm:text-[9px] md:text-[10px] lg:text-xs xl:text-sm',
      fullValue: formatCurrency(value)
    };
  } else if (absValue >= 1000000) {
    // 1M+ - Use SI prefixing
    const millions = absValue / 1000000;
    const formatted = millions.toFixed(2);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}M`,
      sizeClass: 'text-[7px] sm:text-[9px] md:text-[10px] lg:text-xs xl:text-sm',
      fullValue: formatCurrency(value)
    };
  } else if (absValue >= 100000) {
    // 100K+ - Use K prefixing
    const thousands = absValue / 1000;
    const formatted = thousands.toFixed(0);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}K`,
      sizeClass: 'text-[8px] sm:text-[10px] md:text-xs lg:text-sm xl:text-base',
      fullValue: formatCurrency(value)
    };
  } else {
    // Under 100K - Full format
    return {
      display: formatCurrency(value),
      sizeClass: 'text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg',
      fullValue: formatCurrency(value)
    };
  }
};

const AssetLiabilityOverview = ({ netWorthData: propNetWorthData, isPartnerMode = false }) => {
  const [netWorthData, setNetWorthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // If net worth data is provided as prop, use it instead of fetching
  useEffect(() => {
    // If we have prop data, use it
    if (propNetWorthData) {
      console.log('🔍 AssetLiabilityOverview using provided net worth data');
      setNetWorthData(propNetWorthData);
      setLoading(false);
      return;
    }

    // If prop data is null (parent is still fetching), don't fetch ourselves - wait for parent
    // This prevents duplicate fetching and race conditions
    console.log('⏳ AssetLiabilityOverview waiting for parent to provide net worth data');
    setLoading(true);
  }, [propNetWorthData]);

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Asset & Liability Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-xl h-24 w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Asset & Liability Overview</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-amber-700 text-sm">⚠️ {error}</p>
        </div>
      </div>
    );
  }

  if (!netWorthData) {
    return null;
  }

  // Safe number helper to prevent NaN values
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const {
    netWorth,
    totalAssets,
    totalLiabilities,
    regularAccountsTotal,
    breakdown
  } = netWorthData;

  // Debug logging to identify NaN sources
  console.log('🔍 AssetLiabilityOverview Debug:', {
    netWorth: netWorth,
    totalAssets: totalAssets,
    totalLiabilities: totalLiabilities,
    regularAccountsTotal: regularAccountsTotal,
    breakdown: breakdown,
    netWorthType: typeof netWorth,
    totalAssetsType: typeof totalAssets,
    totalLiabilitiesType: typeof totalLiabilities,
    regularAccountsTotalType: typeof regularAccountsTotal,
    netWorthIsNaN: isNaN(netWorth),
    totalAssetsIsNaN: isNaN(totalAssets),
    totalLiabilitiesIsNaN: isNaN(totalLiabilities),
    regularAccountsTotalIsNaN: isNaN(regularAccountsTotal),
    hasBreakdown: !!breakdown,
    assetsLength: breakdown?.assets?.length || 0,
    liabilitiesLength: breakdown?.liabilities?.length || 0
  });

  // Calculate equity percentages with safe number handling
  const safeNetWorth = safeNumber(netWorth);
  const safeTotalAssets = safeNumber(totalAssets);
  const safeTotalLiabilities = safeNumber(totalLiabilities);
  const safeRegularAccountsTotal = safeNumber(regularAccountsTotal);
  
  // When regularAccountsTotal is undefined/zero, use totalAssets as the base for percentage calculation
  const totalValue = safeTotalAssets + Math.abs(safeRegularAccountsTotal);
  const baseValue = totalValue > 0 ? totalValue : safeTotalAssets;
  const assetPercentage = baseValue > 0 ? (safeTotalAssets / baseValue) * 100 : 0;
  const liquidPercentage = baseValue > 0 ? (Math.abs(safeRegularAccountsTotal) / baseValue) * 100 : 0;

  console.log('🔍 Calculated Percentages Debug:', {
    totalValue: totalValue,
    baseValue: baseValue,
    assetPercentage: assetPercentage,
    liquidPercentage: liquidPercentage,
    totalValueIsNaN: isNaN(totalValue),
    baseValueIsNaN: isNaN(baseValue),
    assetPercentageIsNaN: isNaN(assetPercentage),
    liquidPercentageIsNaN: isNaN(liquidPercentage),
    safeRegularAccountsTotal: safeRegularAccountsTotal
  });

  // Asset type icons
  const getAssetIcon = (assetType, className = "h-full w-full") => {
    switch (assetType) {
      case 'house':
      case 'property':
        return <Home className={className} />;
      case 'car':
        return <DirectionsCar className={className} />;
      default:
        return <Business className={className} />;
    }
  };

  const overviewCards = [
    {
      id: "net-worth",
      title: "True Net Worth",
      value: safeNetWorth,
      description: `Assets - Liabilities + Cash`,
      icon: <Assessment className="h-5 w-5 text-purple-600" />,
      bgColor: "from-purple-50 to-purple-100/90",
      textColor: safeNetWorth >= 0 ? "text-purple-600" : "text-red-600",
      colSpan: "md:col-span-2"
    },
    {
      id: "total-assets",
      title: "Total Assets",
      value: safeTotalAssets,
      description: `${safeNumber(assetPercentage).toFixed(1)}% of total value`,
      icon: <TrendingUp className="h-5 w-5 text-green-600" />,
      bgColor: "from-green-50 to-green-100/90",
      textColor: "text-green-600",
      colSpan: "md:col-span-1"
    },
    {
      id: "total-liabilities",
      title: "Total Liabilities",
      value: safeTotalLiabilities,
      description: `Outstanding loan balances`,
      icon: <TrendingDown className="h-5 w-5 text-red-600" />,
      bgColor: "from-red-50 to-red-100/90",
      textColor: "text-red-600",
      colSpan: "md:col-span-1"
    }
  ];

  // Only show if user has assets or liabilities
  if (safeTotalAssets === 0 && safeTotalLiabilities === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Asset & Liability Overview</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <div className="text-blue-500 text-4xl mb-2">🏠</div>
          <h3 className="text-lg font-semibold text-blue-700 mb-2">Track Your Assets & Loans</h3>
          <p className="text-blue-600 mb-4">
            Add your assets (homes, cars, properties) and their associated loans to get a complete picture of your net worth.
          </p>
          <button 
            onClick={() => window.location.href = '/wallet'}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Add Assets & Loans
          </button>
        </div>
      </div>
    );
  }

  // Helper function to get display name for accounts
  const getAccountDisplayName = (account) => {
    // Try different name fields in order of preference
    if (account.name && account.name.trim()) {
      return account.name.trim();
    }
    
    if (account.officialName && account.officialName.trim()) {
      return account.officialName.trim();
    }
    
    // Fallback to institution + mask
    if (account.institutionName && account.mask) {
      return `${account.institutionName} ...${account.mask}`;
    }
    
    if (account.institutionName) {
      return account.institutionName;
    }
    
    // Last resort fallbacks
    if (account.accountType) {
      return account.accountType;
    }
    
    return 'Unknown Account';
  };

  // Helper function to get account balance with multiple fallbacks
  const getAccountBalance = (account) => {
    // For assets (checking/savings), try these fields
    if (account.type === 'depository') {
      return account.currentBalance || account.availableBalance || account.balance || 0;
    }
    
    // For liabilities (credit cards), use current balance
    if (account.type === 'credit') {
      return account.currentBalance || account.balance || 0;
    }
    
    // For loans, use current balance
    if (account.type === 'loan') {
      return account.currentBalance || account.balance || 0;
    }
    
    // Generic fallback
    return account.currentBalance || account.availableBalance || account.balance || 0;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Asset & Liability Overview</h2>
      
      {/* Summary Cards */}
      
             <div className="grid grid-cols-3 gap-2 sm:gap-3">
                 {overviewCards.map(card => {
                   // Format value with SI prefixes for large numbers
                   const valueInfo = formatLargeValue(card.value);
                   
                   return (
                     <div 
                       key={card.id} 
                       className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-2 sm:p-3 md:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] flex flex-col items-center"
                     >
                       {/* Icon - On its own line, centered */}
                       <div className={`bg-gradient-to-br ${card.bgColor} p-1 sm:p-1.5 md:p-2 rounded-lg sm:rounded-xl shadow-md flex-shrink-0 mb-2 sm:mb-2.5 flex items-center justify-center`}>
                         <div className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex items-center justify-center">
                           {React.cloneElement(card.icon, { className: "h-full w-full" })}
                         </div>
                       </div>
                       {/* Title - Centered */}
                       <div className="text-[9px] sm:text-xs md:text-sm font-medium text-gray-600 text-center mb-1 sm:mb-1.5 px-1 leading-tight line-clamp-2">
                         {card.title}
                       </div>
                       {/* Value - Centered, with SI prefixes for large numbers */}
                       <div 
                         className={`${valueInfo.sizeClass} font-bold ${card.textColor} drop-shadow-sm mb-0.5 sm:mb-1 text-center whitespace-nowrap px-1`}
                         title={valueInfo.fullValue}
                       >
                         {valueInfo.display}
                       </div>
                       {/* Description - Centered */}
                       <div className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 text-center px-1 leading-tight">
                         {card.description}
                       </div>
                     </div>
                   );
                 })}
             </div>

             {/* Detailed Breakdown */}
       {(breakdown?.assets?.length > 0 || breakdown?.liabilities?.length > 0) && (
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           {/* Assets */}
           {breakdown?.assets?.length > 0 && (
             <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl p-3 sm:p-4 md:p-6 border border-green-100">
               <h3 className="text-base sm:text-lg font-semibold text-green-700 mb-3 sm:mb-4 flex items-center">
                 <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                 Assets ({breakdown.assets.length})
               </h3>
               <div className="grid grid-cols-2 gap-2 sm:gap-3">
                 {breakdown.assets.map((asset, index) => {
                   const balance = getAccountBalance(asset);
                   const valueInfo = formatLargeValue(balance);
                   
                   return (
                     <div key={asset._id || asset.accountId || index} className="flex flex-col items-center p-2 sm:p-3 bg-white/60 rounded-lg border border-green-100">
                       {/* Icon - On its own line, centered */}
                       <div className="bg-green-100 p-1 sm:p-1.5 md:p-2 rounded-lg mb-1.5 sm:mb-2 flex items-center justify-center">
                         <div className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-green-600 flex items-center justify-center">
                           {getAssetIcon(asset.assetType || 'default', "h-full w-full")}
                         </div>
                       </div>
                       {/* Account Name - Centered */}
                       <div className="text-[9px] sm:text-xs md:text-sm font-medium text-gray-800 text-center mb-1 px-1 leading-tight line-clamp-2">
                         {getAccountDisplayName(asset)}
                       </div>
                       {/* Account Details - Centered */}
                       <div className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 text-center mb-1 px-1 leading-tight">
                         {asset.institutionName && asset.mask && (
                           <span>{asset.institutionName} •••{asset.mask}</span>
                         )}
                         {asset.accountType && (
                           <span className="capitalize"> • {asset.accountType}</span>
                         )}
                       </div>
                       {/* Value - On its own line, centered, with SI prefixes */}
                       <div 
                         className={`${valueInfo.sizeClass} font-semibold text-green-700 text-center whitespace-nowrap px-1`}
                         title={valueInfo.fullValue}
                       >
                         {valueInfo.display}
                       </div>
                     </div>
                   );
                 })}
               </div>
             </div>
           )}

           {/* Liabilities */}
           {breakdown?.liabilities?.length > 0 && (
             <div className="bg-gradient-to-br from-white to-red-50/30 rounded-xl p-3 sm:p-4 md:p-6 border border-red-100">
               <h3 className="text-base sm:text-lg font-semibold text-red-700 mb-3 sm:mb-4 flex items-center">
                 <AccountBalance className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                 Liabilities ({breakdown.liabilities.length})
               </h3>
               <div className="grid grid-cols-2 gap-2 sm:gap-3">
                 {breakdown.liabilities.map((liability, index) => {
                   const balance = getAccountBalance(liability);
                   const valueInfo = formatLargeValue(Math.abs(balance));
                   
                   return (
                     <div key={liability._id || liability.accountId || index} className="flex flex-col items-center p-2 sm:p-3 bg-white/60 rounded-lg border border-red-100">
                       {/* Icon - On its own line, centered */}
                       <div className="bg-red-100 p-1 sm:p-1.5 md:p-2 rounded-lg mb-1.5 sm:mb-2 flex items-center justify-center">
                         <div className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex items-center justify-center">
                           <AccountBalance className="h-full w-full" />
                         </div>
                       </div>
                       {/* Account Name - Centered */}
                       <div className="text-[9px] sm:text-xs md:text-sm font-medium text-gray-800 text-center mb-1 px-1 leading-tight line-clamp-2">
                         {getAccountDisplayName(liability)}
                       </div>
                       {/* Account Details - Centered */}
                       <div className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 text-center mb-1 px-1 leading-tight">
                         {liability.institutionName && liability.mask && (
                           <span>{liability.institutionName} •••{liability.mask}</span>
                         )}
                         {liability.accountType && (
                           <span className="capitalize"> • {liability.accountType}</span>
                         )}
                         {liability.monthlyPayment && (
                           <span> • ${liability.monthlyPayment}/month</span>
                         )}
                       </div>
                       {/* Value - On its own line, centered, with SI prefixes */}
                       <div 
                         className={`${valueInfo.sizeClass} font-semibold text-red-700 text-center whitespace-nowrap px-1`}
                         title={`-${valueInfo.fullValue}`}
                       >
                         -{valueInfo.display}
                       </div>
                     </div>
                   );
                 })}
               </div>
             </div>
           )}
         </div>
       )}

             {/* Net Worth Explanation */}
       <div className="bg-gradient-to-br from-blue-50 to-indigo-50/30 rounded-xl p-4 border border-blue-100">
         <div className="text-sm text-blue-700">
           <strong>Net Worth Calculation:</strong> Assets ({formatCurrency(totalAssets)}) - Liabilities ({formatCurrency(totalLiabilities)}) + Cash/Accounts ({formatCurrency(regularAccountsTotal || 0)}) = <strong>{formatCurrency(netWorth)}</strong>
         </div>
       </div>
    </div>
  );
};

export default AssetLiabilityOverview; 